import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { projectsData } from '../i18n/translations';
import { MapPin, CheckCircle2, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

const ProjectDetail = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const project = projectsData.find(p => p.slug === slug);

  if (!project) {
    return <Navigate to={language === 'es' ? '/promociones' : '/en/projects'} replace />;
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  return (
    <div className="min-h-screen" data-testid="project-detail-page">
      {/* Back Button */}
      <div className="bg-[#E8F2F7] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to={language === 'es' ? '/promociones' : '/en/projects'}
            className="inline-flex items-center space-x-2 text-gray-700 hover:text-[#356C8A] transition-colors"
            data-testid="back-to-projects-link"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">{t.projects.title}</span>
          </Link>
        </div>
      </div>

      {/* Hero Section with Gallery */}
      <section className="relative h-[60vh] bg-gray-900">
        <div className="relative h-full overflow-hidden">
          <img
            src={`/${project.images[currentImageIndex]}`}
            alt={`${project.name} - ${currentImageIndex + 1}`}
            className="w-full h-full object-cover"
            data-testid="project-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Gallery Controls */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
            data-testid="gallery-prev-button"
          >
            <ChevronLeft className="h-6 w-6 text-[#356C8A]" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
            data-testid="gallery-next-button"
          >
            <ChevronRight className="h-6 w-6 text-[#356C8A]" />
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 px-4 py-2 bg-black/70 text-white rounded-full text-sm font-medium">
            {currentImageIndex + 1} / {project.images.length}
          </div>

          {/* Project Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-4xl sm:text-5xl font-bold mb-2" data-testid="project-name">
                {project.name}
              </h1>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span className="text-lg">{project.location}</span>
                </div>
                <div className="px-4 py-1 bg-[#356C8A] rounded-full font-bold">
                  {project.soldPercentage}% {t.projects.status.sold}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div data-testid="project-description">
                <h2 className="text-3xl font-bold text-[#356C8A] mb-4">
                  {project.name}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {project.description[language]}
                </p>
              </div>

              {/* Features */}
              <div data-testid="project-features">
                <h3 className="text-2xl font-bold text-[#356C8A] mb-6">
                  {t.projects.details.features}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {project.features[language].map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-6 w-6 text-[#356C8A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Thumbnail Gallery */}
              <div data-testid="project-gallery-thumbnails">
                <h3 className="text-2xl font-bold text-[#356C8A] mb-6">
                  {t.projects.details.gallery}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {project.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-24 rounded-lg overflow-hidden transition-all ${
                        currentImageIndex === index
                          ? 'ring-4 ring-[#356C8A]'
                          : 'hover:opacity-80'
                      }`}
                      data-testid={`gallery-thumbnail-${index}`}
                    >
                      <img
                        src={`/${image}`}
                        alt={`${project.name} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                <p
                  className="mt-6 text-xs text-gray-500 italic leading-relaxed border-l-2 border-gray-300 pl-3"
                  data-testid="project-images-disclaimer"
                >
                  {t.projects.details.disclaimer}
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Status Card */}
              <div className="bg-[#E8F2F7] rounded-xl p-6" data-testid="project-status-card">
                <h3 className="text-xl font-bold text-[#356C8A] mb-4">
                  {t.projects.details.status}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.projects.details.location}</span>
                    <span className="font-semibold text-[#356C8A]">{project.location}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-300">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">{t.projects.status.sold}</span>
                      <span className="font-bold text-[#356C8A] text-lg">{project.soldPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-3">
                      <div 
                        className="bg-[#356C8A] h-3 rounded-full transition-all"
                        style={{ width: `${project.soldPercentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-[#356C8A] rounded-xl p-6 text-white" data-testid="project-contact-card">
                <h3 className="text-xl font-bold mb-4">
                  {t.projects.details.contact}
                </h3>
                <p className="text-gray-200 mb-6">
                  {language === 'es' 
                    ? 'Contáctenos para más información sobre esta promoción'
                    : 'Contact us for more information about this project'
                  }
                </p>
                <div className="space-y-3">
                  <a
                    href="tel:+34638281201"
                    className="block w-full px-4 py-3 bg-[#356C8A] text-white text-center rounded-lg font-semibold hover:bg-[#C49A44] transition-colors"
                    data-testid="contact-phone-button"
                  >
                    +34 638 28 12 01
                  </a>
                  <a
                    href="mailto:info@pastoria-serralta.com"
                    className="block w-full px-4 py-3 bg-white text-[#356C8A] text-center rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                    data-testid="contact-email-button"
                  >
                    info@pastoria-serralta.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;